/**
 * Editing rules for the thesis inclusion/exclusion lists (SAAS-913).
 *
 * Pure and separate from the settings form because the interesting behaviour
 * is all in the edge cases — blank entries, duplicates that differ only by
 * case or padding — and none of it needs a rendered component to verify.
 *
 * Criteria are free text by design: the structured `target_*` fields already
 * express a fund's positive structural criteria (industry, stage, geography,
 * business model), and an anti-thesis is the things those enums cannot say.
 */

/** Trim and collapse internal whitespace, so " Crypto  tokens " matches "Crypto tokens". */
export const normalizeCriterion = (value: string): string =>
  value.trim().replace(/\s+/g, " ");

/**
 * Append a criterion, or return the list unchanged when it would be blank or a
 * duplicate. Comparison is case-insensitive: a fund that already excludes
 * "Crypto" gains nothing from a second "crypto" entry, and the AI extractor
 * does not guarantee consistent casing.
 */
export const addCriterion = (list: string[], value: string): string[] => {
  const next = normalizeCriterion(value);
  if (!next) return list;
  const exists = list.some(
    (item) => normalizeCriterion(item).toLowerCase() === next.toLowerCase()
  );
  return exists ? list : [...list, next];
};

/** Remove by index. Out-of-range indices leave the list untouched. */
export const removeCriterion = (list: string[], index: number): string[] =>
  index < 0 || index >= list.length
    ? list
    : list.filter((_, i) => i !== index);

/**
 * Normalise a list arriving from the API before it is edited or saved. The AI
 * writes "No information provided" sentinels into criteria fields the same way
 * it does for the structured ones, and those must not become visible chips.
 */
const PLACEHOLDER_VALUES = new Set([
  "no information provided",
  "not applicable",
  "n/a",
  "none",
  "",
]);

export const sanitizeCriteria = (values: string[] | undefined): string[] => {
  if (!values) return [];
  return values.reduce<string[]>(
    (acc, value) =>
      PLACEHOLDER_VALUES.has(normalizeCriterion(value).toLowerCase())
        ? acc
        : addCriterion(acc, value),
    []
  );
};
